import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Container,
  Button,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Card,
} from "@mui/material";
import { useNotification } from "../../../../../Hooks/useNotification";
import {
  validateEmployeeInfo,
  validateAccessSettings,
} from "./validationUtils";

import ConfirmationDialog from "./ConfirmationDialog";
import FormTabs from "./Tabs";
import httpclient from "../../../../../Utils";
import { useApiErrorHandler } from "../../../../../Hooks/useApiErrorHandler";
import useTokenRefresh from "../../../../../Hooks/useTokenRefresh";

// Steps for the stepper
const steps = [
  "Employee Information",
  "Access Settings",
  // "Assigned Device",
];

const formFields = {
  // Employee Info fields
  firstName: "",
  lastName: "",
  emailAddress: "",
  mobileNumber: "",
  employeeCode: "",
  branch: "",
  role: "",
  profilePic: null,

  // Access Setting fields
  password: "",
  confirmPassword: "",
  status: false,

  // Device fields
  device: "",
};

function AddUser() {
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [dialogAction, setDialogAction] = useState(""); // Track whether "cancel" or "submit"
  const [formErrors, setFormErrors] = useState({});
  const [devices, setDevices] = useState([]);

  const mode = id ? "edit" : "add";
  const [loading, setLoading] = useState(mode === "edit");

  const [formData, setFormData] = useState(formFields);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();
  const { getTokenRefreshed: refresh } = useTokenRefresh();

  // Get activeRole from location state, default to "All" if not provided
  const activeRole = location.state?.activeRole || "All";

  // Fetch user data if in edit mode
  useEffect(() => {
    const fetchUserData = async () => {
      if (mode === "edit" && id) {
        setLoading(true);

        const API_URL = `users/details/${id}`;

        try {
          const res = await httpclient.get(API_URL);

          const { data } = res;

          if (data.status === 200) {
            const dataField = data.data;

            const modifiedData = {
              // Employee Info fields
              firstName: dataField.first_name,
              lastName: dataField.last_name,
              emailAddress: dataField.email,
              mobileNumber: dataField.phone,
              employeeCode: dataField.employee_code,
              branch: dataField.branch_code,
              role: dataField.role_id,
              profilePic: dataField.profile_photo || null,

              // Access Setting fields
              password: "",
              confirmPassword: "",
              status: dataField.status,

              // Device fields
              device: dataField.device_unique_id,
            };
            setFormData(modifiedData);
            setDevices(data.data.devices);
          } else {
            showNotification("error", data.msg);
          }
        } catch (error) {
          if (error.response.status === 401) {
            refresh();
          } else {
            handleApiError(error);
          }
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserData();
  }, [mode, id]);

  const handleTabChange = (event, newValue) => {
    // Only allow tab change if current tab passes validation
    if (newValue > tabIndex) {
      if (tabIndex === 0 && !validateTabAndUpdateErrors(0)) return;
      if (tabIndex === 1 && !validateTabAndUpdateErrors(1)) return;
    }
    setTabIndex(newValue);
  };

  // Validate current tab and update errors state
  const validateTabAndUpdateErrors = (tabIdx) => {
    let isValid = false;
    let newErrors = { ...formErrors };

    switch (tabIdx) {
      case 0:
        const employeeInfoErrors = validateEmployeeInfo(formData);
        if (Object.keys(employeeInfoErrors).length === 0) {
          isValid = true;
        } else {
          newErrors = { ...newErrors, ...employeeInfoErrors };
          // Show notification for the first error
          const firstError = Object.values(employeeInfoErrors)[0];
          showNotification("error", firstError);
        }
        break;
      case 1:
        // Skip password validation in edit mode if passwords are empty
        if (
          mode === "edit" &&
          !formData.password &&
          !formData.confirmPassword
        ) {
          isValid = true;
        } else {
          const accessSettingErrors = validateAccessSettings(formData);
          if (Object.keys(accessSettingErrors).length === 0) {
            isValid = true;
          } else {
            newErrors = { ...newErrors, ...accessSettingErrors };
            // Show notification for the first error
            const firstError = Object.values(accessSettingErrors)[0];
            showNotification("error", firstError);
          }
        }
        break;
      // case 2:
      //   const deviceErrors = validateDeviceAssignment(formData);
      //   if (Object.keys(deviceErrors).length === 0) {
      //     isValid = true;
      //   } else {
      //     newErrors = { ...newErrors, ...deviceErrors };
      //     // Show notification for the first error
      //     showNotification("error", Object.values(deviceErrors)[0]);
      //   }
      //   break;
      default:
        isValid = true;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  // Handle form data changes from any component
  const handleFormChange = (fieldName, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));

    // Clear related errors when a field changes
    if (formErrors[fieldName]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handle profile picture changes specifically
  const handleProfilePicChange = (file) => {
    // Validate file type (only images)
    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    if (file && !validTypes.includes(file.type)) {
      showNotification(
        "error",
        "Please upload a valid image file (JPEG, PNG, or GIF)"
      );
      return;
    }

    const maxSize = 1 * 1024 * 1024; // 1MB in bytes
    if (file && file.size > maxSize) {
      showNotification("error", "Image file size must be less than 1MB");
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      profilePic: file,
    }));

    // Clear any profile pic errors
    if (formErrors.profilePic) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.profilePic;
        return newErrors;
      });
    }
  };

  // Handle navigation between tabs
  const handleNext = () => {
    if (!validateTabAndUpdateErrors(tabIndex)) return;
    setTabIndex(tabIndex + 1);
  };

  const handlePrevious = () => {
    setTabIndex(tabIndex - 1);
  };

  const handleConfirmSubmit = (e) => {
    if (e) e.preventDefault(); // <-- this prevents default form submission
    if (!validateTabAndUpdateErrors(tabIndex)) return;
    setDialogAction("submit");
    setOpenConfirmDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenConfirmDialog(false);
  };

  const getFormattedData = (data) => {
    const formData = new FormData();

    const dataMap = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.emailAddress,
      mobile: data.mobileNumber || "",
      employee_code: data.employeeCode,
      branch_code: data.branch,
      role_id: data.role,
      password: data.password,
      confirm_password: data.confirmPassword,
      device_unique_id: data.device,
      status: data.status ? 1 : 0,
    };

    if (data.profilePic instanceof File) {
      formData.append("profile_photo", data.profilePic);
    } else {
      formData.append("profile_photo", "");
    }

    Object.entries(dataMap).forEach(([key, value]) => {
      formData.append(key, value);
    });

    return formData;
  };

  const handleSubmit = async () => {
    setOpenConfirmDialog(false);

    try {
      setIsSubmitting(true);

      const submissionData = getFormattedData(formData);

      // Remove confirmPassword from the data to be sent to the API
      // delete submissionData.confirmPassword;

      // In edit mode, only send the password if it was changed
      // if (mode === "edit" && !submissionData.password) {
      //   delete submissionData.password;
      // }

      const endpoint = mode === "edit" ? `/users/update/${id}` : "/users/add";

      const res = await httpclient.post(endpoint, submissionData, {
        headers: {
          "Content-Type":
            mode === "edit"
              ? "application/x-www-form-urlencoded"
              : "multipart/form-data",
        },
      });

      const { data } = res;

      if (data.status === 200) {
        showNotification(
          "success",
          mode === "edit"
            ? "User successfully updated!"
            : "User successfully added!"
        );

        // Navigate back to the role-specific path
        const rolePath =
          activeRole === "All"
            ? "/users/all"
            : `/users/${activeRole.toLowerCase().replace(/ /g, "-")}`;

        navigate(rolePath);
      } else {
        showNotification("error", data.message);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    const isFormModified = Object.values(formData).some(
      (value) => value !== "" && value !== false && value !== null
    );

    if (isFormModified) {
      setDialogAction("cancel");
      setOpenConfirmDialog(true);
    } else {
      // Navigate back to the role-specific path
      const rolePath =
        activeRole === "All"
          ? "/users/all"
          : `/users/${activeRole.toLowerCase().replace(/ /g, "-")}`;
      navigate(rolePath);
    }
  };

  if (loading) {
    return (
      <Container
        sx={{
          mt: 4,
          mb: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 2, mb: 2 }} maxWidth="xl">
      <Paper elevation={3} sx={{ overflow: "hidden", borderRadius: "12px" }}>
        <Card sx={{ margin: 3 }}>
          <Box sx={{ width: "100%", p: 2 }}>
            <Stepper activeStep={tabIndex} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Card>

        <FormTabs
          tabIndex={tabIndex}
          formData={formData}
          handleTabChange={handleTabChange}
          handleFormChange={handleFormChange}
          handleProfilePicChange={handleProfilePicChange}
          formErrors={formErrors}
          isSubmitting={isSubmitting}
          mode={mode}
          devices={devices}
        />

        {/* Global Navigation Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            p: 3,
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Box>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isSubmitting}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
          </Box>
          <Box>
            {tabIndex > 0 && (
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={isSubmitting}
                sx={{ mr: 2, textTransform: "none" }}
              >
                Previous
              </Button>
            )}
            {tabIndex < 1 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isSubmitting}
                sx={{ backgroundColor: "#7152F3", textTransform: "none" }}
              >
                Next
              </Button>
            )}
            {((mode === "add" && tabIndex === 1) || mode === "edit") && (
              <Button
                type="button"
                variant="contained"
                onClick={(e) => handleConfirmSubmit(e)}
                disabled={isSubmitting}
                sx={{
                  backgroundColor: "#7152F3",
                  textTransform: "none",
                  position: "relative",
                  marginLeft: 1,
                }}
              >
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
                {mode === "edit" ? "Update User" : "Save & Complete"}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {openConfirmDialog && (
        <ConfirmationDialog
          openConfirmDialog={openConfirmDialog}
          handleCloseDialog={handleCloseDialog}
          dialogAction={dialogAction}
          mode={mode}
          handleSubmit={handleSubmit}
        />
      )}
    </Container>
  );
}

export default AddUser;
